/**
 * Enemy — an entity that walks the Path toward the gate. Engine-agnostic (no
 * Phaser): BattleScene syncs a sprite to `distance`. Carries resolved stats
 * (already scaled by difficulty), status timers, and its current blocking state.
 */
import type { Path, PathPoint } from '@/battle/Path';
import { DEFAULT_STATS, type Stats } from '@/battle/Stats';

export class Enemy {
  defId = '';
  active = false;
  /** Which walk lane (path index) this enemy follows on a branching stage.
   *  0 on single-path stages. Set at spawn from its wave group's `lane`. */
  lane = 0;
  /** Scalar progress along the path, in world units. */
  distance = 0;
  stats: Stats = DEFAULT_STATS;
  hp = 0;
  maxHp = 0;
  leakCost = 1;
  bounty = 0;
  xp = 0;
  flying = false;
  /** Boss enemies cause an instant defeat if they reach the gate. */
  isBoss = false;
  /** Immune to slow/freeze (resistKeyword "slowImmune") — Frost can't chill it. */
  slowImmune = false;

  // --- statuses (remaining-duration model; no absolute clock needed) ---
  /** Movement multiplier in (0,1]; 1 = normal speed. */
  slowFactor = 1;
  slowRemaining = 0;
  burnDps = 0;
  burnRemaining = 0;
  /** Sunder: flat armor reduction while active (the anti-armor melee debuff). */
  armorReduction = 0;
  sunderRemaining = 0;

  // --- combat / blocking ---
  /** Slot id of the melee unit currently blocking/engaging it, or null. */
  engagedUnitSlot: string | null = null;
  attackCooldown = 0;
  /** Marked once killed so bounty/death is awarded exactly once. */
  dead = false;

  spawn(
    defId: string,
    stats: Stats,
    leakCost: number,
    bounty: number,
    xp: number,
    flying: boolean,
    isBoss: boolean,
    lane = 0,
  ): void {
    this.defId = defId;
    this.stats = stats;
    this.hp = stats.hp;
    this.maxHp = stats.hp;
    this.leakCost = leakCost;
    this.bounty = bounty;
    this.xp = xp;
    this.flying = flying;
    this.isBoss = isBoss;
    this.lane = lane;
    this.distance = 0;
    this.slowFactor = 1;
    this.slowRemaining = 0;
    this.burnDps = 0;
    this.burnRemaining = 0;
    this.armorReduction = 0;
    this.sunderRemaining = 0;
    this.engagedUnitSlot = null;
    this.attackCooldown = 0;
    this.dead = false;
    this.active = true;
  }

  position(path: Path): PathPoint {
    return path.getPointAt(this.distance);
  }

  /** Progress 0..1 toward the gate (for HUD/lane-ribbon). */
  progress(pathLength: number): number {
    return pathLength > 0 ? this.distance / pathLength : 0;
  }
}
